import { Stack } from "@mui/material"
import { FC, useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { ReservationAud } from "shared/reservationAud"
import { ReservationAudThingTypeMrn } from "shared/reservationAudData"
import { SharedAccessPatternOsEnum } from "shared/sharedAccessPatternsData"
import { Thing } from "shared/thing"
import { useAppSelector } from "src/app/hooks"
import { selectTenantId } from "src/features/me/meSlice"
import { useGetThingsQuery } from "src/features/things/thingsApi"
import { selectThingsByThingTypeMrnFilter } from "src/features/things/thingsSlice"
import { OsReservationAuditTrailList } from "./OsReservationAuditTrailList"

type OsReservationAuditTrailProps = {
  reservationId: string
  /**
   * `reservation.migratedFromSl2ReservationId` — the pre-migration V1 reservation
   * this one was materialized from, when it was (DEV-193).
   *
   * The backend read-through returns the V1 reservation's audit rows alongside
   * the V2 ones, and each row keeps its OWN `reservationId` so history is never
   * re-attributed. That means the store filter below has to accept the ancestor
   * id too — filtering on `reservationId` alone silently drops every
   * pre-migration event and makes the whole read-through invisible.
   */
  migratedFromReservationId?: string
}
export const OsReservationAuditTrail: FC<OsReservationAuditTrailProps> = ({
  reservationId,
  migratedFromReservationId,
}) => {
  const tenantId = useAppSelector(selectTenantId)

  const { t } = useTranslation()

  const q = useGetThingsQuery(
    {
      params: {
        "ap.name": SharedAccessPatternOsEnum.GetReservationAudsById,
        "ap.reservationId": reservationId,
        "ap.tenantId": tenantId,
        tenantId,
      },
    },
    {
      skip: !tenantId,
    },
  )

  const acceptedReservationIds = useMemo(
    () =>
      new Set(
        [reservationId, migratedFromReservationId].filter(Boolean) as string[],
      ),
    [reservationId, migratedFromReservationId],
  )

  const filter = useCallback(
    (t: Thing) => acceptedReservationIds.has(t.reservationId),
    [acceptedReservationIds],
  )

  const reservationAuds = useAppSelector((s) =>
    selectThingsByThingTypeMrnFilter(s, ReservationAudThingTypeMrn, filter),
  ) as ReservationAud[] | undefined

  return (
    <Stack data-ui="UI-SL2W-057" direction="column" spacing={1}>
      <OsReservationAuditTrailList
        data={reservationAuds || []}
        currentReservationId={reservationId}
        isFetching={q.isFetching}
        isLoading={q.isLoading}
        refetch={q.refetch}
      />
    </Stack>
  )
}
